import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';
import toast from 'react-hot-toast';

const CartDrawer: React.FC = () => {
  const { cart, isOpen, closeCart, updateItem, removeItem } = useCartStore();

  const handleUpdateQuantity = async (id: string, quantity: number) => {
    try {
      await updateItem(id, quantity);
    } catch {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await removeItem(id);
      toast.success('Item removed');
    } catch {
      toast.error('Failed to remove item');
    }
  };

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="overlay"
            onClick={closeCart}
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-light">
              <div className="flex items-center space-x-2">
                <ShoppingBag size={18} className="text-primary" />
                <span className="font-serif text-lg tracking-widest uppercase">
                  Your Bag
                </span>
                {cart && cart.item_count > 0 && (
                  <span className="text-xs text-nude">({cart.item_count})</span>
                )}
              </div>
              <button onClick={closeCart}>
                <X size={20} className="text-primary hover:text-nude transition-colors" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {!cart || cart.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full space-y-4">
                  <ShoppingBag size={40} className="text-nude" />
                  <p className="text-xs tracking-widest uppercase text-nude">
                    Your bag is empty
                  </p>
                  <button onClick={closeCart} className="btn-secondary text-xs">
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {cart.items.map((item) => (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="flex space-x-4"
                    >
                      {/* Image */}
                      <div className="w-20 h-24 bg-light flex-shrink-0 overflow-hidden">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-light" />
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <Link
                            to={`/products/${item.product_slug}`}
                            onClick={closeCart}
                            className="text-xs tracking-wide uppercase font-medium hover:text-nude transition-colors"
                          >
                            {item.product_name}
                          </Link>
                          <button onClick={() => handleRemove(item.id)}>
                            <X size={14} className="text-nude hover:text-primary transition-colors" />
                          </button>
                        </div>

                        {(item.size || item.color) && (
                          <p className="text-xs text-nude mt-1">
                            {item.size && `Size: ${item.size}`}
                            {item.size && item.color && ' / '}
                            {item.color && `Color: ${item.color}`}
                          </p>
                        )}

                        <div className="flex items-center justify-between mt-3">
                          {/* Quantity */}
                          <div className="flex items-center space-x-3 border border-light">
                            <button
                              onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                              className="p-1 hover:bg-light transition-colors"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="text-xs w-4 text-center">{item.quantity}</span>
                            <button
                              onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                              disabled={item.quantity >= item.stock_quantity}
                              className="p-1 hover:bg-light transition-colors disabled:opacity-40"
                            >
                              <Plus size={12} />
                            </button>
                          </div>

                          {/* Price */}
                          <p className="text-xs tracking-wide">
                            ${(Number(item.price) * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {cart && cart.items.length > 0 && (
              <div className="border-t border-light px-6 py-6 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs tracking-widest uppercase">Subtotal</span>
                  <span className="text-sm font-medium">
                    ${Number(cart.subtotal).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-nude tracking-wide">
                  Shipping and taxes calculated at checkout
                </p>
                <Link
                  to="/checkout"
                  onClick={closeCart}
                  className="btn-primary w-full text-center block"
                >
                  Proceed to Checkout
                </Link>
                <button
                  onClick={closeCart}
                  className="btn-secondary w-full text-center"
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CartDrawer;